import { useSearchParams } from 'react-router-dom';
import type { SingleValue } from 'react-select';

import { TaxonomyType } from '@revelio/data-access';
import { MAX_PRIMARY_ENTITY_SELECTIONS } from '@revelio/iso-utility';

import { carryOverEmbedParams } from '../../../../shared/hooks/useHideNav';
import { kibanaLogger } from '../../../../shared/logging/kibana-logger';
import { useActivityTaxonomyConfig } from '../../../../shared/selection-lists/hooks/useActivityTaxonomyConfig';
import { useCurrentReferenceTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useReferenceTaxonomyVersions';
import { useCurrentRoleTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import type { SavedView } from '../../components/SavedViews/useSavedViews';
import { useToast } from '../../hooks/useToast';
import { CORRECTION_PREFERENCE_FILTER_IDS } from '../constants/filter-constants';
import { createDefaultFilterMap } from '../contexts/defaultFilters';
import { useFilterContext } from '../contexts/FilterProvider';
import {
  type Filter,
  type FilterId,
  type FilterSelectionsMap,
  FilterType,
  type Option,
  type PrimaryFilterId,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import { isPrimaryFilterId } from '../utils/common';
import { getConsistencyKey } from '../utils/consistencyKey';
import {
  enforceMaxSelections,
  readPersistedPrimarySelections,
} from '../utils/primaryFilterUtils';
import { decodeSavedViewFilters } from '../utils/savedViewUrl';
import { trackTaxonomyFiltersRemoved } from '../utils/trackTaxonomyFiltersRemoved';
import {
  isValidId,
  splitLegacyCompanyIndustrySelections,
} from '../utils/validate-parsed-filters';
import { stripDisabledActivityFilters } from '../utils/validateActivityFilters';
import { validateTaxonomyFilters } from '../utils/validateTaxonomyFilters';
import { useTaxonomyVersionToast } from './useTaxonomyVersionToast';

const KIBANA_CONTEXT = 'saved-filter-set-apply';

const primaryFilterHasNoSelections = (
  filterMap: FilterSelectionsMap,
  primaryFilterId: PrimaryFilterId
): boolean => {
  const filter = filterMap.get(primaryFilterId);
  if (!filter || filter.type !== FilterType.TREE) return false;
  const primaryCount = filter.primarySelections?.length ?? 0;
  const selectionCount = filter.selections?.length ?? 0;
  return primaryCount === 0 || selectionCount === 0;
};

const validateTreeFilterIds = (
  filter: Filter,
  toast: ReturnType<typeof useToast>,
  savedSetId: string
) => {
  if (filter.type !== FilterType.TREE) return filter;

  const persistedSelections = readPersistedPrimarySelections(
    filter.primarySelections ?? filter.selections
  );
  const validPrimarySelections = persistedSelections.filter((s) =>
    isValidId(s.id)
  );
  const validSelections =
    filter.selections?.filter((s) => isValidId(s.id)) || [];
  const selections = filter.selections || [];
  const hasInvalidSelections =
    (persistedSelections.length > 0 || selections.length > 0) &&
    (validPrimarySelections.length !== persistedSelections.length ||
      validSelections.length !== selections.length);

  if (hasInvalidSelections) {
    kibanaLogger?.warn(
      `Saved filter set carried invalid ${filter.id} selections, removed on apply: saved_set_id=${savedSetId}`,
      { context: KIBANA_CONTEXT }
    );
    toast({
      title: `Your ${filter.name} filters have been removed from your saved view. Please re-select them.`,
      status: 'warning',
      duration: 6000,
      isClosable: true,
      position: 'top-right',
      id: 'saved-filter-set-removed',
    });
  }
  // Sets saved before the global ceiling existed can carry more selections
  // than the app now allows — cap them on rehydration.
  const cappedPrimarySelections = enforceMaxSelections(
    validPrimarySelections,
    MAX_PRIMARY_ENTITY_SELECTIONS
  );

  return {
    ...filter,
    primarySelections: cappedPrimarySelections,
    selections: filter.isPrimary
      ? cappedPrimarySelections
      : filter.selections?.filter((s) => isValidId(s.id)) || [],
  };
};

export const useApplySavedView = () => {
  const { primaryFilter, revelioPageId } = useTabContentConfig();
  const { dispatch, activeFilterMap } = useFilterContext();
  const { version: roleTaxonomyVersion } = useCurrentRoleTaxonomyVersion();
  const { version: skillTaxonomyVersion } = useCurrentReferenceTaxonomyVersion({
    taxonomyType: TaxonomyType.Skill,
  });
  const activityTaxonomyConfig = useActivityTaxonomyConfig();
  const toast = useToast();
  const showTaxonomyVersionToast = useTaxonomyVersionToast();
  const [, setSearchParams] = useSearchParams();

  return async (value: SingleValue<Option<SavedView>> | undefined) => {
    if (!value) return;
    const filterUrl = value.entity?.url;
    if (!filterUrl) return;

    try {
      const params = new URLSearchParams(filterUrl.split('?')[1] ?? '');
      const parsedRaw = decodeSavedViewFilters(filterUrl);

      if (!parsedRaw) {
        kibanaLogger?.warn(
          `Saved filter set is in an unreadable older format: saved_set_id=${value.value}`,
          { context: KIBANA_CONTEXT }
        );
        toast({
          id: 'saved-view-unreadable',
          title: `The "${value.entity?.name ?? value.label}" view was saved in an older format and can't be applied. Please re-create it.`,
          status: 'warning',
          duration: 8000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      // carry over embed/chrome params (e.g. `hideNav`)
      setSearchParams(
        (prev) => carryOverEmbedParams(prev, new URLSearchParams(params)),
        { replace: true }
      );

      const taxonomyConfig: TaxonomyConfig = {
        roleTaxonomyVersion,
        skillTaxonomyVersion,
        ...activityTaxonomyConfig,
      };

      const taxonomyValidatedFilters = validateTaxonomyFilters({
        filters: stripDisabledActivityFilters(parsedRaw, taxonomyConfig),
        taxonomyConfig,
        showTaxonomyVersionToast,
        onTaxonomyFiltersRemoved: (removal) =>
          trackTaxonomyFiltersRemoved(removal, {
            source: 'saved_set',
            savedSetId: value.value,
          }),
      });

      // // Validate filter IDs before creating the Map
      const validatedFilterEntries: [FilterId, Filter][] =
        taxonomyValidatedFilters.map(
          ([filterId, filter]: [FilterId, Filter]) => {
            const validatedFilter = validateTreeFilterIds(
              filter,
              toast,
              value.value
            );
            return [filterId, validatedFilter];
          }
        );

      const filterMap = (() => {
        const map = new Map([
          ...createDefaultFilterMap(primaryFilter?.id, taxonomyConfig),
          ...validatedFilterEntries,
        ]) as FilterSelectionsMap;
        if (
          !primaryFilter ||
          !isPrimaryFilterId(primaryFilter.id) ||
          !primaryFilterHasNoSelections(map, primaryFilter.id)
        ) {
          return map;
        }
        const defaultMap = createDefaultFilterMap(
          primaryFilter.id,
          taxonomyConfig
        );
        const defaultPrimaryFilter = defaultMap.get(primaryFilter.id);
        if (!defaultPrimaryFilter) return map;
        return new Map([...map, [primaryFilter.id, defaultPrimaryFilter]]);
      })();

      for (const filterId of CORRECTION_PREFERENCE_FILTER_IDS) {
        const applied = filterMap.get(filterId);
        const current = activeFilterMap.get(filterId);
        if (
          applied?.type === FilterType.STATIC_LIST &&
          applied.selections.length === 0 &&
          current?.type === FilterType.STATIC_LIST &&
          current.selections.length > 0
        ) {
          filterMap.set(filterId, current);
        }
      }

      // Sets saved before the Company/Industry split carry industries inside
      // the Company filter — land them in the Industry filter instead.
      splitLegacyCompanyIndustrySelections(filterMap, primaryFilter?.id);

      dispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          consistencyKey: getConsistencyKey({
            revelioPageId,
            primaryFilterId: primaryFilter?.id,
          }),
        },
      });

      toast({
        id: 'saved-view-applied',
        title: `Applied "${value.entity?.name ?? value.label}"`,
        status: 'success',
        duration: 8000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Failed to apply saved view', error);
      kibanaLogger?.error(
        `Failed to apply saved filter set: saved_set_id=${value.value} ${error instanceof Error ? error.message : String(error)}`,
        { context: KIBANA_CONTEXT }
      );
    }
  };
};
